import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarPlus, Clock, CheckCircle, CaretRight, Spinner, ArrowsCounterClockwise } from "@phosphor-icons/react";
import API from "../lib/api";

const COLUMNS = [
  {
    key: "awaiting_admin",
    label: "Awaiting your response",
    hint: "Supplier proposed — counter or accept",
    badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border-amber-200 dark:border-amber-800",
    accent: "border-l-4 border-amber-500",
    icon: Clock,
  },
  {
    key: "awaiting_supplier",
    label: "Awaiting supplier",
    hint: "You've countered — waiting on them",
    badge: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200 border-sky-200 dark:border-sky-800",
    accent: "border-l-4 border-sky-500",
    icon: ArrowsCounterClockwise,
  },
  {
    key: "ready_to_convert",
    label: "Ready to convert",
    hint: "Date agreed — turn into a job",
    badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800",
    accent: "border-l-4 border-emerald-500",
    icon: CheckCircle,
  },
];

function Card({ item, columnKey, navigate }) {
  const dt = (columnKey === "ready_to_convert" || columnKey === "awaiting_supplier")
    ? (item.admin_counter_start_date || item.proposed_start_date)
    : item.proposed_start_date;
  const note = (columnKey === "awaiting_supplier") ? item.admin_counter_note : item.start_date_note;
  const goTo = () => navigate(`/job-requests/${item.id}`);
  return (
    <button
      onClick={goTo}
      data-testid={`pending-sd-card-${item.id}`}
      className="w-full text-left bg-card hover:bg-muted/50 border border-border rounded-md p-2.5 transition-colors group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[10px] font-mono text-muted-foreground">{item.reference}</span>
            {item.start_date_round > 1 && (
              <span className="text-[9px] bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded-full font-medium">Round {item.start_date_round}</span>
            )}
          </div>
          <p className="text-xs font-semibold truncate">{item.title}</p>
          {item.client_name && <p className="text-[10px] text-muted-foreground truncate">{item.client_name}</p>}
        </div>
        <CaretRight size={12} className="text-muted-foreground flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
      </div>
      <div className="mt-2 pt-2 border-t border-border/60 space-y-1">
        <div className="flex items-center gap-1.5 text-[10px]">
          <CalendarPlus size={11} className="text-muted-foreground" />
          <span className="font-medium">{dt}</span>
          {item.vendor_name && <span className="text-muted-foreground">· {item.vendor_name}</span>}
        </div>
        {note && <p className="text-[10px] italic text-muted-foreground line-clamp-2">"{note}"</p>}
      </div>
    </button>
  );
}

export default function PendingStartDatesWidget({ className = "" }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/job-requests/pending-start-dates");
      setData(data || { buckets: {}, totals: { all: 0 } });
    } catch { setData({ buckets: {}, totals: { all: 0 } }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Phase 229f — pulse + refresh when relevant notifications fire
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    const onNotif = (e) => {
      const t = e.detail?.type || "";
      if (t.includes("start_date") || t.includes("counter") || t.includes("propose") || t.includes("quote_invitation")) {
        setPulse(true);
        load();
        setTimeout(() => setPulse(false), 2400);
      }
    };
    window.addEventListener("fixli:notification", onNotif);
    return () => window.removeEventListener("fixli:notification", onNotif);
  }, [load]);

  const total = data?.totals?.all || 0;
  // Hide completely when there's nothing to surface
  if (!loading && total === 0) return null;

  return (
    <div className={`border border-border rounded-md bg-card transition-all duration-500 ${pulse ? "ring-2 ring-amber-400 shadow-lg" : ""} ${className}`} data-testid="pending-start-dates-widget">
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors"
        data-testid="pending-sd-toggle"
      >
        <div className="flex items-center gap-2">
          <CalendarPlus size={16} className="text-primary" />
          <h3 className="text-sm font-[Chivo] font-semibold">Start-date negotiations</h3>
          {total > 0 && (
            <span className="text-[10px] bg-primary/15 text-primary px-2 py-0.5 rounded-full font-medium" data-testid="pending-sd-total">{total}</span>
          )}
          {data?.totals?.awaiting_admin > 0 && (
            <span className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 px-2 py-0.5 rounded-full font-medium" data-testid="pending-sd-awaiting-admin-badge">
              {data.totals.awaiting_admin} need your response
            </span>
          )}
        </div>
        <CaretRight size={14} className={`text-muted-foreground transition-transform ${collapsed ? "" : "rotate-90"}`} />
      </button>
      {!collapsed && (
        <div className="px-4 pb-4">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-4">
              <Spinner size={14} className="animate-spin" /> Loading…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {COLUMNS.map((col) => {
                const items = (data?.buckets?.[col.key]) || [];
                const Icon = col.icon;
                return (
                  <div
                    key={col.key}
                    data-testid={`pending-sd-col-${col.key}`}
                    className={`bg-muted/30 rounded-md p-2.5 ${col.accent} min-h-[120px]`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5">
                        <Icon size={12} />
                        <p className="text-[11px] font-semibold uppercase tracking-wider">{col.label}</p>
                      </div>
                      <span className={`text-[10px] border px-1.5 py-0.5 rounded-full font-medium ${col.badge}`}>{items.length}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mb-2">{col.hint}</p>
                    {items.length === 0 ? (
                      <p className="text-[10px] italic text-muted-foreground py-3 text-center">None</p>
                    ) : (
                      <div className="space-y-2">
                        {items.map((it) => (
                          <Card key={it.id} item={it} columnKey={col.key} navigate={navigate} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
